/**
 * 📦 DUNGEON ITEMS SYSTEM
 *
 * Consumables, materials, buffs, and other non-equipment items.
 * Organized by category: Potions, Materials, Buffs, Quest Items
 */

// ============================================================
// ITEM CATEGORIES
// ============================================================
export const ITEM_CATEGORIES = {
  POTION: 'potion',
  MATERIAL: 'material',
  GEMSTONE: 'gemstone',
  BUFF: 'buff',
  QUEST: 'quest',
  MISC: 'misc',
  CUBE: 'cube',
};

// ============================================================
// CONSUMABLE ITEMS (Potions, Food, etc.)
// ============================================================
export const CONSUMABLES = {
  // Health Potions
  minorHealthPotion: {
    id: 'minorHealthPotion',
    name: 'Minor Health Potion',
    icon: '🧪',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'common',
    description: 'Restores 50 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 50,
    },
    usable: true,
    sellValue: 10,
  },

  healthPotion: {
    id: 'healthPotion',
    name: 'Health Potion',
    icon: '🧪',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'uncommon',
    description: 'Restores 150 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 150,
    },
    usable: true,
    sellValue: 30,
  },

  greaterHealthPotion: {
    id: 'greaterHealthPotion',
    name: 'Greater Health Potion',
    icon: '🧪',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'rare',
    description: 'Restores 400 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 400,
    },
    usable: true,
    sellValue: 80,
  },

  superiorHealthPotion: {
    id: 'superiorHealthPotion',
    name: 'Superior Health Potion',
    icon: '🧪',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'epic',
    description: 'Restores 1000 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 1000,
    },
    usable: true,
    sellValue: 200,
  },

  // Buff Potions
  strengthPotion: {
    id: 'strengthPotion',
    name: 'Potion of Strength',
    icon: '💪',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'uncommon',
    description: '+20% Attack for 5 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'attack',
      value: 0.20,
      duration: 300, // seconds
    },
    usable: true,
    sellValue: 50,
  },

  defensePotion: {
    id: 'defensePotion',
    name: 'Potion of Defense',
    icon: '🛡️',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'uncommon',
    description: '+20% Defense for 5 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'defense',
      value: 0.20,
      duration: 300,
    },
    usable: true,
    sellValue: 50,
  },

  swiftnessPotion: {
    id: 'swiftnessPotion',
    name: 'Potion of Swiftness',
    icon: '⚡',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'rare',
    description: '+2 Speed for 5 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'speed',
      value: 2,
      duration: 300,
    },
    usable: true,
    sellValue: 75,
  },

  elixirOfPower: {
    id: 'elixirOfPower',
    name: 'Elixir of Power',
    icon: '⚗️',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'epic',
    description: '+30% Attack and +30% Defense for 10 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stats: {
        attack: 0.30,
        defense: 0.30,
      },
      duration: 600,
    },
    usable: true,
    sellValue: 200,
  },

  // Food Items
  bread: {
    id: 'bread',
    name: 'Bread',
    icon: '🍞',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'common',
    description: 'Restores 25 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 25,
    },
    usable: true,
    sellValue: 2,
  },

  cookedMeat: {
    id: 'cookedMeat',
    name: 'Cooked Meat',
    icon: '🍖',
    category: ITEM_CATEGORIES.POTION,
    rarity: 'common',
    description: 'Restores 75 HP',
    stackSize: 99,
    effect: {
      type: 'heal',
      value: 75,
    },
    usable: true,
    sellValue: 8,
  },
};

// ============================================================
// CRAFTING MATERIALS
// ============================================================
// NOTE: Materials are defined in DUNGEON_RESOURCES (resources.js)
// They are imported and re-exported here with item metadata added.
// All materials are stored in playerCharacter.inventory with category='material'
// ============================================================

import { DUNGEON_RESOURCES } from './resources';

// Re-export materials from DUNGEON_RESOURCES with item metadata
export const MATERIALS = Object.entries(DUNGEON_RESOURCES).reduce((acc, [id, resource]) => {
  acc[id] = {
    ...resource,
    category: ITEM_CATEGORIES.MATERIAL,
    usable: false, // Materials are crafting components, not consumables
  };
  return acc;
}, {});

// ============================================================
// BUFF ITEMS (Scrolls, Talismans, etc.)
// ============================================================
export const BUFF_ITEMS = {
  scrollOfFortitude: {
    id: 'scrollOfFortitude',
    name: 'Scroll of Fortitude',
    icon: '📜',
    category: ITEM_CATEGORIES.BUFF,
    rarity: 'uncommon',
    description: '+100 Max HP for 10 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'health',
      value: 100,
      duration: 600,
    },
    usable: true,
    sellValue: 40,
  },

  scrollOfPrecision: {
    id: 'scrollOfPrecision',
    name: 'Scroll of Precision',
    icon: '📜',
    category: ITEM_CATEGORIES.BUFF,
    rarity: 'rare',
    description: '+15% Crit Chance for 10 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'critChance',
      value: 0.15,
      duration: 600,
    },
    usable: true,
    sellValue: 100,
  },

  blessingToken: {
    id: 'blessingToken',
    name: 'Blessing Token',
    icon: '🪙',
    category: ITEM_CATEGORIES.BUFF,
    rarity: 'epic',
    description: '+50% XP gain for 30 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'xpBonus',
      value: 0.50,
      duration: 1800,
    },
    usable: true,
    sellValue: 250,
  },

  luckyCharm: {
    id: 'luckyCharm',
    name: 'Lucky Charm',
    icon: '🍀',
    category: ITEM_CATEGORIES.BUFF,
    rarity: 'rare',
    description: '+20% Loot Quality for 15 minutes',
    stackSize: 99,
    effect: {
      type: 'buff',
      stat: 'lootQuality',
      value: 0.20,
      duration: 900,
    },
    usable: true,
    sellValue: 150,
  },
};

// ============================================================
// QUEST ITEMS (Special items for quests)
// ============================================================
export const QUEST_ITEMS = {
  ancientKey: {
    id: 'ancientKey',
    name: 'Ancient Key',
    icon: '🗝️',
    category: ITEM_CATEGORIES.QUEST,
    rarity: 'rare',
    description: 'An old key that might unlock something important.',
    stackSize: 1,
    sellValue: 0, // Cannot sell quest items
    questId: 'unlock_ancient_door',
  },

  mysteriosCrystal: {
    id: 'mysteriosCrystal',
    name: 'Mysterious Crystal',
    icon: '💎',
    category: ITEM_CATEGORIES.QUEST,
    rarity: 'epic',
    description: 'A crystal pulsing with unknown energy.',
    stackSize: 1,
    sellValue: 0,
    questId: 'crystal_research',
  },

  dragonEgg: {
    id: 'dragonEgg',
    name: 'Dragon Egg',
    icon: '🥚',
    category: ITEM_CATEGORIES.QUEST,
    rarity: 'legendary',
    description: 'A dormant dragon egg. Handle with care.',
    stackSize: 1,
    sellValue: 0,
    questId: 'hatch_dragon',
  },
};

// ============================================================
// GEMSTONES (Forging System Currency)
// Drop from bosses only, used to apply scrolls to equipment
// ============================================================
export const GEMSTONE_ITEMS = {
  ruby: {
    id: 'ruby',
    name: 'Ruby',
    icon: '🔴',
    category: ITEM_CATEGORIES.GEMSTONE,
    rarity: 'rare',
    description: 'A fiery red gemstone. Used to forge Attack scrolls.',
    stackSize: 9999,
    isGemstone: true,
    forgingStat: 'attack',
    color: '#e74c3c',
    sellValue: 25,
  },
  sapphire: {
    id: 'sapphire',
    name: 'Sapphire',
    icon: '🔵',
    category: ITEM_CATEGORIES.GEMSTONE,
    rarity: 'rare',
    description: 'A deep blue gemstone. Used to forge Defense scrolls.',
    stackSize: 9999,
    isGemstone: true,
    forgingStat: 'defense',
    color: '#3498db',
    sellValue: 25,
  },
  emerald: {
    id: 'emerald',
    name: 'Emerald',
    icon: '🟢',
    category: ITEM_CATEGORIES.GEMSTONE,
    rarity: 'rare',
    description: 'A vibrant green gemstone. Used to forge Health scrolls.',
    stackSize: 9999,
    isGemstone: true,
    forgingStat: 'health',
    color: '#2ecc71',
    sellValue: 25,
  },
  topaz: {
    id: 'topaz',
    name: 'Topaz',
    icon: '🟡',
    category: ITEM_CATEGORIES.GEMSTONE,
    rarity: 'rare',
    description: 'A golden gemstone. Used to forge Critical scrolls.',
    stackSize: 9999,
    isGemstone: true,
    forgingStat: 'critChance',
    color: '#f1c40f',
    sellValue: 25,
  },
  amethyst: {
    id: 'amethyst',
    name: 'Amethyst',
    icon: '🟣',
    category: ITEM_CATEGORIES.GEMSTONE,
    rarity: 'rare',
    description: 'A purple gemstone. Used to forge Speed scrolls.',
    stackSize: 9999,
    isGemstone: true,
    forgingStat: 'speed',
    color: '#9b59b6',
    sellValue: 25,
  },
};

// ============================================================
// MISCELLANEOUS ITEMS
// ============================================================
export const MISC_ITEMS = {
  goldCoin: {
    id: 'goldCoin',
    name: 'Gold Coin',
    icon: '💰',
    category: ITEM_CATEGORIES.MISC,
    rarity: 'common',
    description: 'Currency used for trading.',
    stackSize: 9999,
    sellValue: 1,
  },

  treasureMap: {
    id: 'treasureMap',
    name: 'Treasure Map',
    icon: '🗺️',
    category: ITEM_CATEGORIES.MISC,
    rarity: 'rare',
    description: 'Reveals a hidden treasure location.',
    stackSize: 99,
    effect: {
      type: 'reveal_treasure',
    },
    usable: true,
    sellValue: 100,
  },

  teleportScroll: {
    id: 'teleportScroll',
    name: 'Teleport Scroll',
    icon: '🌀',
    category: ITEM_CATEGORIES.MISC,
    rarity: 'uncommon',
    description: 'Instantly return to dungeon entrance.',
    stackSize: 99,
    effect: {
      type: 'teleport_entrance',
    },
    usable: true,
    sellValue: 50,
  },

  cleanSlateScroll: {
    id: 'cleanSlateScroll',
    name: 'Clean Slate Scroll',
    icon: '📜',
    category: ITEM_CATEGORIES.MISC,
    rarity: 'epic',
    description: 'Resets all gem slots on an equipment piece, removing applied scrolls and failed slots.',
    stackSize: 99,
    effect: {
      type: 'clean_slate',
    },
    usable: true,
    requiresTarget: 'equipment', // Indicates this item needs a target
    sellValue: 500,
  },
};

// ============================================================
// CUBES (Boss Drops Only - For Cubing System)
// ============================================================
export const CUBE_ITEMS = {
  occultCube: {
    id: 'occultCube',
    name: 'Occult Cube',
    icon: '🎲',
    category: ITEM_CATEGORIES.CUBE,
    rarity: 'uncommon',
    description: 'Rerolls potential lines. Cannot upgrade grade.',
    stackSize: 99,
    isCube: true,
    sellValue: 50,
  },
  masterCube: {
    id: 'masterCube',
    name: 'Master Cube',
    icon: '💎',
    category: ITEM_CATEGORIES.CUBE,
    rarity: 'rare',
    description: 'Rerolls potential lines. Small chance to upgrade grade.',
    stackSize: 99,
    isCube: true,
    sellValue: 150,
  },
  meisterCube: {
    id: 'meisterCube',
    name: 'Meister Cube',
    icon: '🔮',
    category: ITEM_CATEGORIES.CUBE,
    rarity: 'epic',
    description: 'Rerolls potential lines. Good chance to upgrade grade.',
    stackSize: 99,
    isCube: true,
    sellValue: 350,
  },
  redCube: {
    id: 'redCube',
    name: 'Red Cube',
    icon: '❤️',
    category: ITEM_CATEGORIES.CUBE,
    rarity: 'epic',
    description: 'Rerolls potential lines. High chance to upgrade grade.',
    stackSize: 99,
    isCube: true,
    sellValue: 500,
  },
  blackCube: {
    id: 'blackCube',
    name: 'Black Cube',
    icon: '🖤',
    category: ITEM_CATEGORIES.CUBE,
    rarity: 'legendary',
    description: 'Best chance to upgrade. Can choose to keep current lines.',
    stackSize: 99,
    isCube: true,
    sellValue: 1000,
  },
};

// ============================================================
// ALL ITEMS REGISTRY
// ============================================================
export const ALL_ITEMS = {
  ...CONSUMABLES,
  ...MATERIALS,
  ...GEMSTONE_ITEMS,
  ...BUFF_ITEMS,
  ...QUEST_ITEMS,
  ...MISC_ITEMS,
  ...CUBE_ITEMS,
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get items by category
 */
export function getItemsByCategory(category) {
  return Object.values(ALL_ITEMS).filter(item => item.category === category);
}

/**
 * Get items by rarity
 */
export function getItemsByRarity(rarity) {
  return Object.values(ALL_ITEMS).filter(item => item.rarity === rarity);
}

/**
 * Get item by ID
 */
export function getItem(itemId) {
  return ALL_ITEMS[itemId] || null;
}

/**
 * Check if item is usable
 */
export function isUsableItem(itemId) {
  const item = getItem(itemId);
  return item?.usable === true;
}

/**
 * Get item categories
 */
export function getCategories() {
  return Object.values(ITEM_CATEGORIES);
}
